import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import CollapsibleGroup from 'components/Common/CollapsibleGroup/CollapsibleGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'
import config from 'constants/config'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import AgentHubMarkerSlider from './AgentHubMarkerSlider'
import Label from 'LuxKit/Label/Label'
import Tooltip from 'LuxKit/Tooltip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { setAgentExtraEarnings } from 'actions/AgentHubActions'

const MAX_MARKUP_PERCENTAGE = 20
const checkIcon = <LineCheckIcon />

const FullWidthSheet = styled(Sheet)`
  width: 100%;
`

const ContentSpacer = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
`

interface Props {
  offerId: string
  offerTotal: number
  /** The "valued up to" retail price, used to calculate customer savings */
  retailValue?: number
  packageId?: string
  optionId?: string
  offer?: App.AgentHubOfferPayload
  /** When provided, bypasses the commission hook entirely (used in checkout where commission comes from the cart endpoint) */
  commissionPercentage?: number
  /** LuxPlus+ member price — when provided, a marker is shown on the slider indicating
   * the commission level at which the customer total matches LuxPlus+ pricing */
  memberPrice?: number
}

const LP_PLUS_MARKER_THRESHOLD = 3
const SLIDER_HALF_STEP = 0.25

function isSliderAtLuxPlusStep(sliderValue: number, luxPlusSliderValue: number | null): boolean {
  return luxPlusSliderValue !== null && Math.abs(sliderValue - luxPlusSliderValue) < SLIDER_HALF_STEP
}

function AgentCommissionPanel({ offerId, offerTotal, retailValue, packageId, optionId, offer, commissionPercentage: commissionPercentageProp, memberPrice }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const customerView = useAppSelector(state => state.agentHub.customerView)
  const hasExternalCommission = commissionPercentageProp !== undefined

  const { commissionPercentage: hookCommissionPercentage, packagesCommission, dateRangesCommission } = useAgentHubCommission(
    hasExternalCommission ? '' : offerId,
    hasExternalCommission ? undefined : offer,
  )

  const dateRangeCommission = optionId ? dateRangesCommission[optionId] : null
  const packageCommission = packageId ? packagesCommission.find(pkg => pkg.packageId === packageId) : null

  const effectiveCommission = useMemo(() => {
    if (hasExternalCommission) {
      return commissionPercentageProp
    }
    const dateRangeExtra = dateRangeCommission?.commissionPercentage ?? 0
    if (packageCommission?.commissionPercentage) {
      return packageCommission.commissionPercentage + dateRangeExtra
    }
    return hookCommissionPercentage + dateRangeExtra
  }, [hasExternalCommission, commissionPercentageProp, dateRangeCommission, hookCommissionPercentage, packageCommission])

  const [sliderValue, setSliderValue] = useState(effectiveCommission)

  useEffect(() => {
    setSliderValue(effectiveCommission)
    dispatch(setAgentExtraEarnings(0))
  }, [dispatch, effectiveCommission])

  const rrpLabel = intl.formatMessage({
    id: 'w2DVWZE/dB',
    defaultMessage: 'RRP',
    description: 'Marker label on the commission slider indicating the recommended retail price point',
  })

  const luxPlusLabel = intl.formatMessage({
    id: '9tNQdhfMpv',
    defaultMessage: 'LuxPlus+',
    description: 'Marker label on the commission slider indicating the LuxPlus+ member price point',
  })

  const luxPlusSliderValue = useMemo(() => {
    if (memberPrice && offerTotal > 0) {
      const value = effectiveCommission + (memberPrice / offerTotal - 1) * 100
      const sliderDifference = effectiveCommission - value
      if (value >= 0 && sliderDifference >= LP_PLUS_MARKER_THRESHOLD) {
        return value
      }
    }
    return null
  }, [effectiveCommission, memberPrice, offerTotal])

  const markers = useMemo(() => {
    const result: Array<{ value: number; label: string; kind?: 'default' | 'highlight' }> = []
    if (effectiveCommission > 0) {
      result.push({ value: effectiveCommission, label: rrpLabel })
    }
    if (luxPlusSliderValue !== null) {
      result.push({ value: luxPlusSliderValue, label: luxPlusLabel, kind: 'highlight' })
    }
    return result
  }, [effectiveCommission, luxPlusSliderValue, rrpLabel, luxPlusLabel])

  const { totalEarnings, customerTotal, customerSavings } = useMemo(() => {
    const adjustmentFromRRP = sliderValue - effectiveCommission
    const totalEarnings = offerTotal * (sliderValue / 100)
    const rawCustomerTotal = offerTotal + offerTotal * (adjustmentFromRRP / 100)
    const customerTotal = isSliderAtLuxPlusStep(sliderValue, luxPlusSliderValue) && memberPrice !== undefined
      ? memberPrice
      : Math.round(rawCustomerTotal)
    const customerSavings = retailValue ? Math.max(0, retailValue - customerTotal) : 0
    return { totalEarnings, customerTotal, customerSavings }
  }, [sliderValue, effectiveCommission, offerTotal, retailValue, luxPlusSliderValue, memberPrice])

  const handleSliderChange = useCallback((value: number) => {
    setSliderValue(value)
    const extraEarnings = Math.round(offerTotal * (value - effectiveCommission)) / 100
    dispatch(setAgentExtraEarnings(extraEarnings))
  }, [dispatch, effectiveCommission, offerTotal])

  if (!config.agentHub.isEnabled || !effectiveCommission || customerView) {
    return null
  }

  const tooltipDescription = intl.formatMessage({
    id: 'v0iDPUkv4k',
    defaultMessage: 'Adjust the slider to increase or decrease your commission. This changes your client\'s price, but your net payout from Agent Hub does not change',
    description: 'Tooltip explaining how the commission adjustment slider works for agents',
  })

  return (
    <FullWidthSheet padding={8} border="neutral-five" borderWidth={1} borderRadius="L">
      <CollapsibleGroup
        label={(
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            <BodyTextBlock
              variant="medium"
              weight="bold"
              startIcon={<Tooltip description={tooltipDescription} portaled><LineInfoCircleIcon size="S" /></Tooltip>}
            >
              {intl.formatMessage({
                id: 'iCgyAIrS/q',
                defaultMessage: 'Your commission rate',
                description: 'Heading for the agent commission adjustment panel',
              })}
            </BodyTextBlock>
            <Label kind="tertiary" variant="highlight">
              {intl.formatMessage({
                id: 'WU+wxV8/pw',
                defaultMessage: '{percentage}% applied',
                description: 'Label showing the currently applied commission percentage',
              }, { percentage: effectiveCommission })}
            </Label>
          </Group>
        )}
        noPadding
      >
        <ContentSpacer gap={16}>
          <AgentHubMarkerSlider
            min={0}
            max={MAX_MARKUP_PERCENTAGE}
            step={0.5}
            value={sliderValue}
            markers={markers}
            minLabel={intl.formatMessage({
              id: 'l7qhFuQgex',
              defaultMessage: '{percentage}% Markup',
              description: 'Minimum markup label on the commission slider',
            }, { percentage: 0 })}
            maxLabel={intl.formatMessage({
              id: 'MphldqLDJA',
              defaultMessage: '{percentage}% Markup',
              description: 'Maximum markup label on the commission slider',
            }, { percentage: MAX_MARKUP_PERCENTAGE })}
            onChange={handleSliderChange}
          />
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'jU6AXam7VI',
                defaultMessage: 'Your earnings',
                description: 'Label for the agent earnings row in the commission panel',
              })}
            </BodyText>
            <BodyText variant="medium" weight="bold" colour="highlight-secondary">
              <FormatCurrency value={totalEarnings} />
            </BodyText>
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between">
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'Z843oXzh6h',
                defaultMessage: 'Customer total',
                description: 'Label for the customer total price row in the commission panel',
              })}
            </BodyText>
            <BodyText variant="medium" weight="bold" colour="highlight-secondary">
              <FormatCurrency value={customerTotal} />
            </BodyText>
          </Group>
          {customerSavings > 0 && (
            <Sheet background="highlight-primary" borderRadius="S" padding={12}>
              <BodyTextBlock variant="medium" weight="bold" startIcon={checkIcon}>
                {intl.formatMessage({
                  id: 'lYn1kwFbJ+',
                  defaultMessage: 'Customer saves {amount}',
                  description: 'Text showing how much the customer saves, with the currency amount interpolated',
                }, { amount: <FormatCurrency value={customerSavings} /> })}
              </BodyTextBlock>
            </Sheet>
          )}
        </ContentSpacer>
      </CollapsibleGroup>
    </FullWidthSheet>
  )
}

export default AgentCommissionPanel
